import { LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import type { ReactNodeViewProps } from '@tiptap/react';
import { Trash2 } from 'lucide-react';
import { memo } from 'react';
export const ZyITLabGoalNodeView: React.FC<ReactNodeViewProps> = memo(
    ({ node, updateAttributes, selected, editor, getPos, deleteNode, decorations }) => {
        const isEditorReadonly = useIsEditorReadonly(editor);
        const pos = getPos();
        const { nodeIndex } = useSiblingNodeInfo({
            editor,
            getPos,
            nodeTypeName: 'zyITLabGoal',
        });
        const resolvedPos = typeof pos === 'number' ? pos : 0;
        const zyITLab = editor.state.doc.resolve(resolvedPos).node(-3);
        const vmImagesNode = (() => {
            for (let i = 0; i < zyITLab.childCount; i++) {
                if (zyITLab.child(i).type.name === 'zyITLabVmImages') {
                    return zyITLab.child(i);
                }
            }
            return null;
        })();
        const vmImages = vmImagesNode ? Array.from({ length: vmImagesNode.childCount }, (_, i) => vmImagesNode.child(i)) : [];

        const setTestRun = (testIndex: number) => {
            if (typeof resolvedPos !== 'number') return;
            const tr = editor.state.tr;
            node.forEach((child, offset, index) => {
                tr.setNodeMarkup(resolvedPos + 1 + offset, undefined, {
                    ...child.attrs,
                    run: index === testIndex,
                });
            });
            editor.view.dispatch(tr);
        };

        const updateTestAttr = (testIndex: number, key: 'command' | 'expected', value: string) => {
            if (typeof resolvedPos !== 'number') return;
            node.forEach((child, offset, index) => {
                if (index !== testIndex) {
                    return;
                }
                editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(resolvedPos + 1 + offset, undefined, {
                        ...child.attrs,
                        [key]: value,
                    }),
                );
            });
        };

        return (
            <CommonNodeViewWrapper
                decorations={decorations}
                label={`Goal ${nodeIndex + 1}`}
                selected={selected}
                containerClassName="mb-2 rounded border border-dashed px-4 py-6 border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-950"
                contentClassName="p-0"
            >
                <div contentEditable={false}>
                    <div className="mb-3 grid gap-3 md:grid-cols-[120px_1fr]">
                        <div>
                            <label
                                className={LABEL_CLASS}
                                contentEditable={false}
                            >
                                Points
                            </label>
                            <Input
                                type="number"
                                min={1}
                                max={99}
                                className="mt-1"
                                value={node.attrs.points ?? 1}
                                onChange={e => updateAttributes({ points: Number(e.target.value) })}
                                readOnly={isEditorReadonly}
                                contentEditable={false}
                            />
                        </div>
                        <div>
                            <label
                                className={LABEL_CLASS}
                                contentEditable={false}
                            >
                                Completion message
                            </label>
                            <Input
                                className="mt-1"
                                value={node.attrs.message || ''}
                                onChange={e => updateAttributes({ message: e.target.value })}
                                readOnly={isEditorReadonly}
                                contentEditable={false}
                            />
                        </div>
                    </div>

                    {node.childCount === 0 ? (
                        <p className="text-sm text-gray-500">Add a VM image to configure validation tests.</p>
                    ) : (
                        <div className="space-y-4">
                            {Array.from({ length: node.childCount }, (_, testIndex) => {
                                const test = node.child(testIndex);
                                const vmImage = vmImages[testIndex];
                                const vmTitle = vmImage?.attrs.title || vmImage?.attrs.name || `VM ${testIndex + 1}`;
                                return (
                                    <div
                                        key={testIndex}
                                        className="rounded border border-gray-200 p-3 dark:border-gray-700"
                                    >
                                        <div className="mb-2 flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Validation · {vmTitle}
                                            </span>
                                            {vmImages.length > 1 && (
                                                <label className="flex items-center gap-2 text-sm text-gray-600">
                                                    <input
                                                        type="radio"
                                                        name={`goal-${resolvedPos}-vm-run`}
                                                        checked={Boolean(test.attrs.run)}
                                                        onChange={() => setTestRun(testIndex)}
                                                        disabled={isEditorReadonly}
                                                    />
                                                    Run on this VM
                                                </label>
                                            )}
                                        </div>
                                        <div className="mb-2">
                                            <label
                                                className={LABEL_CLASS}
                                                contentEditable={false}
                                            >
                                                Command
                                            </label>
                                            <textarea
                                                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                                rows={3}
                                                value={test.attrs.command || ''}
                                                onChange={e => updateTestAttr(testIndex, 'command', e.target.value)}
                                                disabled={isEditorReadonly}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                className={LABEL_CLASS}
                                                contentEditable={false}
                                            >
                                                Expected output
                                            </label>
                                            <textarea
                                                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                                rows={2}
                                                value={test.attrs.expected || ''}
                                                onChange={e => updateTestAttr(testIndex, 'expected', e.target.value)}
                                                disabled={isEditorReadonly}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-3 flex justify-end">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isEditorReadonly}
                            onClick={() => deleteNode()}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove goal
                        </Button>
                    </div>
                </div>
            </CommonNodeViewWrapper>
        );
    },
);

ZyITLabGoalNodeView.displayName = 'ZyITLabGoalNodeView';
